// PHASE_1 / B1 — leave UX capability derivation (ScopeGate convenience only).
//
// PURE helper: derives the leave capability strings the <ScopeGate> component
// hides actions behind from the caller's RESOLVED effective permissions
// (useEffectivePermissions capability set) — never from the navigation hat.
// The dual-role hat (ADR-014 §1) is a navigation concept; approval authority
// is a permission (a hat-derived gate made an HR director's "/" dashboard show
// "0 pending" while requests awaited their decision). This is CONVENIENCE
// ONLY — the server re-checks every request (ADR-017; the frontend is never a
// trust boundary, ADR-008).
//
// Capability vocabulary (mirrors the route's (module, action) seam):
//   * leave.file    — file + submit + cancel own request (every employee)
//   * leave.approve — approve a submitted request (permission-derived)
//   * leave.reject  — reject a submitted request (permission-derived)

export const LEAVE_CAP_FILE = "leave.file";
export const LEAVE_CAP_APPROVE = "leave.approve";
export const LEAVE_CAP_REJECT = "leave.reject";

/**
 * The catalog cell the server's decision endpoints gate on. BOTH decision
 * verbs derive from it: POST /v1/leave/requests/:id/approve AND /:id/reject
 * require matrix `leave.approve` (reject is the negative approval decision —
 * see services/platform-graph/src/routes/leave.ts).
 */
export const LEAVE_DECIDE_CELL = "leave.approve";

/**
 * The leave capabilities the caller's resolved permission set exposes in the
 * UI. Self-service filing is UNCONDITIONAL: it is inherent to being an
 * employee and is not modeled as a catalog cell here — deriving it from the
 * default-closed matrix would hide the file-leave button from every employee.
 * Only the decision (admin) capabilities are permission-derived. Returned as
 * a readonly Set for O(1) <ScopeGate> membership tests.
 *
 * @param allowed the caller's resolved `<module>.<action>` capability set
 *   (useEffectivePermissions().capabilities) — empty while loading/on error,
 *   so decision affordances are default-closed until permissions resolve.
 */
export function leaveCapabilities(allowed: ReadonlySet<string>): ReadonlySet<string> {
  const caps = new Set<string>([LEAVE_CAP_FILE]);
  if (allowed.has(LEAVE_DECIDE_CELL)) {
    caps.add(LEAVE_CAP_APPROVE);
    caps.add(LEAVE_CAP_REJECT);
  }
  return caps;
}
